import { 
    gql, 
    useQuery 
} from "@apollo/client";
import { 
    FindAllCustomersAdminQueryQuery, 
    QueryFindAllCustomersAdminArgs 
} from "../../../../../schemaTypes";
import { useCallback } from "react";

export const findAllCustomersAdminQuery = gql`
  query FindAllCustomersAdminQuery(
    $page: Int!
    $pageSize: Int!
    $communityFilter: String
    $ambassadorFilter: Boolean
    $searchText: String
    $duration: String
    $sortBy: String
    $sortOrder: String
  ) {
    findAllCustomersAdmin(
      page: $page
      pageSize: $pageSize
      communityFilter: $communityFilter
      ambassadorFilter: $ambassadorFilter
      searchText: $searchText
      duration: $duration
      sortBy: $sortBy
      sortOrder: $sortOrder
    ) {
      data {
        user{
          id
          firstname
          lastname
          username
          isAmbassador
        }
        community{
          id
          alias
          name
        }
        totalSpend
        location
        suspended
        isLocked
        isDeactivated
      }
      total
      page
      pageSize
      totalPages
    }
  }
`;

export interface WithFindAllCustomersAdmin {
  data: FindAllCustomersAdminQueryQuery["findAllCustomersAdmin"]["data"]
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  loading: boolean;
  loadMore: () => void;
  hasMoreCustomers: boolean;
}

interface Props {
  variables: QueryFindAllCustomersAdminArgs;
  children: (data: WithFindAllCustomersAdmin) => React.ReactElement | null;
}

export const FindAllCustomersAdminController: React.FC<Props> = ({
  children,
  variables,
}) => {
  const { data, loading, fetchMore } = useQuery<
    FindAllCustomersAdminQueryQuery,
    QueryFindAllCustomersAdminArgs
  >(findAllCustomersAdminQuery, {
    variables,
    notifyOnNetworkStatusChange: true,
  });

  const findAllCustomersAdminData = data?.findAllCustomersAdmin?.data || [];
  const page = data?.findAllCustomersAdmin?.page || variables.page;
  const pageSize = data?.findAllCustomersAdmin?.pageSize || variables.pageSize;
  const total = data?.findAllCustomersAdmin?.total || 0;
  const totalPages = data?.findAllCustomersAdmin?.totalPages || 0;
  const hasMoreCustomers = page < totalPages;

  const loadMore = useCallback(() => {
    if (!hasMoreCustomers) return;
    fetchMore({
      variables: {
        ...variables,
        page: page + 1,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult || !fetchMoreResult?.findAllCustomersAdmin) {
          return prev;
        }
        return {
          ...fetchMoreResult,
          findAllCustomersAdmin: {
            ...fetchMoreResult?.findAllCustomersAdmin,
            data: [
              ...(prev?.findAllCustomersAdmin?.data || []),
              ...(fetchMoreResult?.findAllCustomersAdmin?.data || []),
            ],
          },
        };
      },
    });
  }, [fetchMore, variables, page, hasMoreCustomers]);

  return children({
    data: findAllCustomersAdminData as any,
    loading,
    loadMore,
    hasMoreCustomers,
    page,
    pageSize,
    total,
    totalPages,
  });
};